/**
 * Frontend Permission System — mirrors backend permissions.py
 * 
 * Roles: admin, dispatcher, accounts, compliance_officer, viewer
 * Users can hold multiple roles; permissions are the union of all role grants.
 */

const ROLE_PERMISSIONS = {
  admin: new Set(['*']),
  dispatcher: new Set([
    'dashboard.read',
    'schedule.read', 'schedule.write',
    'jobs.read', 'jobs.write',
    'projects.read', 'projects.write',
    'allocations.read', 'allocations.write', 'allocations.send',
    'customers.read', 'customers.write',
    'sites.read', 'sites.write',
    'suppliers.read', 'suppliers.write',
    'equipment.read', 'equipment.write',
    'operators.read', 'operators.write',
    'tips_materials.read', 'tips_materials.write',
    'quotes.read', 'quotes.write',
    'dockets.read', 'dockets.write', 'dockets.submit', 'dockets.delete',
    'compliance.read',
    'compliance_packs.read', 'compliance_packs.write',
    'timesheets.read', 'timesheets.write', 'timesheets.approve',
    'chat.read', 'chat.write',
    'forms.read',
    'reports.read',
    'documents.read', 'documents.write',
    'mobilisation.read', 'mobilisation.write',
    'push_templates.read', 'push_templates.send',
    'notifications.read',
  ]),
  accounts: new Set([
    'dashboard.read',
    'customers.read', 'customers.write',
    'sites.read',
    'dockets.read', 'dockets.write', 'dockets.approve', 'dockets.delete',
    'invoicing.read', 'invoicing.write',
    'reports.read', 'reports.financial',
    'price_lists.read', 'price_lists.write',
    'billing.read',
    'accounting.read', 'accounting.write',
    'quotes.read', 'quotes.write',
    'timesheets.read',
    'payroll.read', 'payroll.write', 'payroll.export',
    'documents.read',
  ]),
  compliance_officer: new Set([
    'dashboard.read',
    'compliance.read', 'compliance.write',
    'compliance_packs.read', 'compliance_packs.write',
    'operators.read',
    'equipment.read',
    'suppliers.read',
    'reports.read',
  ]),
  payroll: new Set([
    'dashboard.read',
    'timesheets.read', 'timesheets.write', 'timesheets.approve',
    'operators.read',
    'dockets.read',
    'reports.read',
    // payroll.read/write/export intentionally excluded — financial data
    // is restricted to admin and accounts roles only.
  ]),
  viewer: new Set([
    'dashboard.read',
    'schedule.read',
    'jobs.read',
    'projects.read',
    'customers.read',
    'sites.read',
    'equipment.read',
    'operators.read',
    'dockets.read',
    'quotes.read',
    'compliance.read',
    'timesheets.read',
    'reports.read',
    'chat.read',
    'suppliers.read',
    'documents.read',
  ]),
};

export const ROLE_LABELS = {
  admin: 'Admin',
  dispatcher: 'Dispatcher',
  accounts: 'Accounts',
  payroll: 'Payroll',
  compliance_officer: 'Compliance',
  viewer: 'Viewer',
};

export const ROLE_DESCRIPTIONS = {
  admin: 'Full access to all features and settings',
  dispatcher: 'Schedule jobs, manage customers, operators, equipment and day-to-day operations',
  accounts: 'Manage dockets, invoicing, price lists, billing and accounting integrations',
  payroll: 'Manage operator timesheets, approve/edit hours, view dockets and reports',
  compliance_officer: 'Manage compliance records, types, reminders and view operator/equipment data',
  viewer: 'Read-only access to schedules, jobs, customers, reports and other data',
};

export const ROLE_COLORS = {
  admin: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-700' },
  dispatcher: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-700' },
  accounts: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-800 dark:text-amber-300', border: 'border-amber-200 dark:border-amber-700' },
  payroll: { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-800 dark:text-teal-300', border: 'border-teal-200 dark:border-teal-700' },
  compliance_officer: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-800 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-700' },
  viewer: { bg: 'bg-zinc-100 dark:bg-zinc-800', text: 'text-zinc-700 dark:text-zinc-300', border: 'border-zinc-200 dark:border-zinc-700' },
};

export const ROLE_ICONS = {
  admin: 'Shield',
  dispatcher: 'UserCog',
  accounts: 'Calculator',
  payroll: 'DollarSign',
  compliance_officer: 'ShieldCheck',
  viewer: 'Eye',
};

// All assignable roles (excluding operator which is handled separately)
export const ASSIGNABLE_ROLES = ['admin', 'dispatcher', 'accounts', 'payroll', 'compliance_officer', 'viewer'];

/**
 * Get the roles array from a user object, handling legacy format
 */
export function getUserRoles(user) {
  if (!user) return [];
  const roles = user.roles;
  if (roles && Array.isArray(roles) && roles.length > 0) return roles;
  const role = user.role;
  if (role) return [role];
  return ['viewer'];
}

/**
 * Check if a user has a specific permission
 */
export function hasPermission(user, permission) {
  const roles = getUserRoles(user);
  
  for (const role of roles) {
    const perms = ROLE_PERMISSIONS[role];
    if (!perms) continue;
    
    // Wildcard = full admin access
    if (perms.has('*')) return true;
    
    // Exact match
    if (perms.has(permission)) return true;
    
    // Resource-level wildcard
    const resource = permission.split('.')[0];
    if (perms.has(`${resource}.*`)) return true;
  }
  
  return false;
}

/**
 * Check if user has ANY of the listed permissions
 */
export function hasAnyPermission(user, permissions) {
  return permissions.some(p => hasPermission(user, p));
}

/**
 * Check if user is an admin (has wildcard)
 */
export function isAdminUser(user) {
  return hasPermission(user, '*');
}
